// Layout pour les pages Acheteur avec menu latéral persistant
import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, ClipboardList, Heart, User, RotateCcw, Zap, Percent, Gift,
  Store, Briefcase, Settings, HelpCircle, ChevronRight, Home, Search,
  ShoppingCart, ArrowLeft, LogOut
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_organize-smart/artifacts/oi1t7sgl_batiassist_logo_transparent.png";

// Menu sections pour acheteurs
const MENU_SECTIONS = [
  {
    title: 'Mon espace acheteur',
    items: [
      { icon: ClipboardList, label: 'Mes commandes', path: '/mes-commandes', color: '#0EA5E9' },
      { icon: Heart, label: 'Mes favoris', path: '/mes-favoris', color: '#EC4899' },
      { icon: User, label: 'Mon profil', path: '/mon-profil', color: '#10B981' },
      { icon: RotateCcw, label: 'Acheter à nouveau', path: '/acheter-a-nouveau', color: '#6366F1' },
    ]
  },
  {
    title: 'Offres & Économies',
    items: [
      { icon: Zap, label: 'Ventes Flash', path: '/ventes-flash', color: '#EF4444', badge: '🔥' },
      { icon: Percent, label: 'Promotions de la semaine', path: '/promotions', color: '#F59E0B' },
      { icon: Gift, label: 'Cartes cadeaux', path: '/cartes-cadeaux', color: '#8B5CF6' },
    ]
  },
  {
    title: 'Espace Professionnel',
    items: [
      { icon: Store, label: 'Devenir vendeur', path: '/batiassist', color: '#F97316' },
      { icon: Briefcase, label: 'Compte Pro (vendeur)', path: '/compte', color: '#3B82F6' },
    ]
  },
  {
    title: 'Aide & Paramètres',
    items: [
      { icon: Settings, label: 'Paramètres', path: '/parametres', color: '#6B7280' },
      { icon: HelpCircle, label: 'Service client', path: '/service-client', color: '#3B82F6' },
    ]
  }
];

export default function AcheteurLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isActive = (path) => location.pathname === path;

  // Sur mobile, on affiche juste le contenu (pas le menu latéral)
  if (isMobile) {
    return <Outlet />;
  }

  // Sur desktop, on affiche le menu à gauche + contenu à droite
  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="acheteur-layout">
      {/* Sidebar Menu - Desktop Only */}
      <aside className="w-80 bg-white border-r border-gray-200 fixed left-0 top-0 bottom-0 overflow-y-auto z-40">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="AbracadaBati" className="h-10 w-auto" />
          </Link>
          <p className="text-xs text-gray-500 mt-2">Bonjour 👋</p>
          <p className="text-sm font-medium text-gray-900">Se connecter / S'inscrire</p>
        </div>

        {/* Navigation */}
        <nav className="p-2">
          {MENU_SECTIONS.map((section, sIdx) => (
            <div key={sIdx} className="mb-4">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {section.title}
              </p>
              <div className="space-y-1">
                {section.items.map((item, iIdx) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  
                  return (
                    <Link
                      key={iIdx}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                        active 
                          ? 'bg-orange-50 border-l-4 border-orange-500' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${item.color}15` }}
                      >
                        <Icon size={20} style={{ color: item.color }} />
                      </div>
                      <span className={`text-sm flex-1 ${active ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {item.label}
                      </span>
                      {item.badge && (
                        <span className="text-sm">{item.badge}</span>
                      )}
                      <ChevronRight size={16} className="text-gray-400" />
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 mt-auto">
          <Link
            to="/produits"
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors text-sm font-medium justify-center"
          >
            <ShoppingCart size={18} />
            Retour à la Marketplace
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-80">
        <Outlet />
      </main>
    </div>
  );
}
